import { useMemo, useState } from 'react';
import { entryItems, entryTabs } from '@/mocks/home';
import SectionHeading from './SectionHeading';

const stateStyles: Record<string, string> = {
  可用: 'bg-secondary-100 text-secondary-800 border-secondary-200',
  限速: 'bg-accent-100 text-accent-800 border-accent-200',
  检测中: 'bg-background-200 text-foreground-600 border-background-300',
};

const typeStyles: Record<string, string> = {
  官网入口: 'bg-primary-100 text-primary-800',
  备用线路: 'bg-accent-100 text-accent-900',
  镜像节点: 'bg-secondary-100 text-secondary-800',
};

export default function EntryStatus() {
  const [activeTab, setActiveTab] = useState('全部');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const list = useMemo(
    () => (activeTab === '全部' ? entryItems : entryItems.filter((item) => item.type === activeTab)),
    [activeTab],
  );

  const handleCopy = async (id: string, addr: string) => {
    try {
      await navigator.clipboard.writeText(addr);
    } catch {
      /* clipboard unavailable, still show feedback */
    }
    setCopiedId(id);
    window.setTimeout(() => setCopiedId(null), 1800);
  };

  return (
    <section id="entries" className="w-full border-b border-background-200/70 bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-8 md:py-20">
        <SectionHeading
          index="01"
          label="Live Entries"
          title="最新可用入口与地址"
          desc="主站入口、备用线路与镜像节点按巡检结果实时排列，标注响应时间与更新时间，换链接防走丢时按顺序尝试即可。"
          extra={
            <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-refresh-line text-sm text-secondary-600" aria-hidden="true"></i>
              </span>
              <span className="whitespace-nowrap text-xs font-label text-foreground-600">
                最近刷新 · 3 分钟前
              </span>
            </div>
          }
        />

        <div className="mt-8 inline-flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
          {entryTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={
                activeTab === tab
                  ? 'cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-xs font-label font-medium text-background-50 md:text-sm'
                  : 'cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-xs font-label text-foreground-600 transition-colors hover:text-primary-600 md:text-sm'
              }
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-hidden rounded-lg border border-background-200">
          <div className="hidden grid-cols-12 items-center gap-4 border-b border-background-200 bg-background-100 px-5 py-3 text-xs font-label text-foreground-500 lg:grid">
            <span className="col-span-4">入口名称</span>
            <span className="col-span-2">类型</span>
            <span className="col-span-2">地区 / 响应</span>
            <span className="col-span-2">状态 / 更新</span>
            <span className="col-span-2 text-right">操作</span>
          </div>

          <ul>
            {list.map((item) => (
              <li
                key={item.id}
                className="grid grid-cols-1 items-center gap-3 border-b border-background-200/70 px-5 py-4 transition-colors last:border-b-0 hover:bg-background-100/70 lg:grid-cols-12 lg:gap-4"
              >
                <div className="lg:col-span-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-label font-medium text-foreground-950">{item.name}</span>
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[11px] font-label ${stateStyles[item.state]}`}
                    >
                      {item.state}
                    </span>
                  </div>
                  <p className="mt-1 font-mono text-xs text-foreground-500">{item.addr}</p>
                </div>

                <div className="lg:col-span-2">
                  <span
                    className={`inline-flex rounded-md px-2.5 py-1 text-[11px] font-label ${typeStyles[item.type]}`}
                  >
                    {item.type}
                  </span>
                </div>

                <div className="flex items-center gap-3 text-xs font-label text-foreground-600 lg:col-span-2 lg:flex-col lg:items-start lg:gap-0">
                  <span>{item.region}</span>
                  <span className="text-foreground-500 lg:mt-1">{item.response}</span>
                </div>

                <div className="flex items-center gap-2 text-xs font-label text-foreground-600 lg:col-span-2 lg:flex-col lg:items-start lg:gap-0">
                  <span
                    className={item.state === '可用' ? 'text-secondary-700' : 'text-foreground-600'}
                  >
                    {item.state === '可用' ? '可正常访问' : item.state === '限速' ? '访问偏慢' : '巡检中'}
                  </span>
                  <span className="text-foreground-500 lg:mt-1">{item.updated}</span>
                </div>

                <div className="flex items-center gap-2 lg:col-span-2 lg:justify-end">
                  <button
                    type="button"
                    onClick={() => handleCopy(item.id, item.addr)}
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-label text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i
                        className={copiedId === item.id ? 'ri-check-line text-sm' : 'ri-file-copy-line text-sm'}
                        aria-hidden="true"
                      ></i>
                    </span>
                    {copiedId === item.id ? '已复制' : '复制'}
                  </button>
                  <a
                    href="#entries"
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-900 px-3 py-1.5 text-xs font-label text-background-50 transition-colors hover:bg-foreground-800"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-arrow-right-up-line text-sm" aria-hidden="true"></i>
                    </span>
                    访问
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-4 flex items-center gap-2 text-xs font-label text-foreground-500">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-information-line text-sm" aria-hidden="true"></i>
          </span>
          列表按巡检顺序排列，若全部线路访问异常，请稍后刷新本页获取最新地址。
        </p>
      </div>
    </section>
  );
}